import { Fragment, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tag, TaptapList } from 'sonata-react-components';
import { DOOH_REPORT_DEFAULT_SCREENS_PAGE_SIZE } from 'sonata-static-storage/lib/entities/doohReport/doohReport.constants';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import ListPagination from '@/components/doohReport/DeliveryDrivers/common/ListPagination';
import PoiBadge from '@/components/doohReport/DeliveryDrivers/common/PoiBadge';
import ScreenMapActions from '@/components/doohReport/DeliveryDrivers/common/ScreenMapActions';
import ScreenTechSpecs from '@/components/doohReport/DeliveryDrivers/common/ScreenTechSpecs';
import {
  DEFAULT_SORT_STATE,
  isDeliveryDriversSortableColumn,
  paginate,
  SCREEN_SORT_ACCESSORS,
  sortByAccessor
} from '@/components/doohReport/DeliveryDrivers/deliveryDrivers.helpers';
import { buildTopScreensGridColumns, getTopScreensColumnLabels, TOP_SCREENS_COLUMNS } from './topScreensColumns';

import type { CSSProperties, ReactNode } from 'react';
import type { DoohReportScreen } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';
import type { TopScreensColumnId } from './topScreensColumns';

import './topScreensList.scss';

type Props = {
  screens: DoohReportScreen[];
  /** Whether a hideable column is currently shown (see the "Columns" menu). */
  isColumnVisible: (columnId: TopScreensColumnId) => boolean;
};

type ColumnCell = (screen: DoohReportScreen) => ReactNode;

/** Screen ids such as `com.broadsign:<uuid>` carry characters that are not valid in a CSS id selector. */
const toScreenDomId = (screenId: string) => screenId.replace(/[^\w-]/g, '_');

/** "Top screens by on-target impact" — the delivery drivers' default view. */
export default function TopScreensList({ screens, isColumnVisible }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatCount, formatOta, formatPercent, notAvailable } = useDoohReportFormatters();

  const [sortState, setSortState] = useState(DEFAULT_SORT_STATE);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DOOH_REPORT_DEFAULT_SCREENS_PAGE_SIZE);

  const labels = getTopScreensColumnLabels(formatMessage);

  const sortedScreens = useMemo(
    () => sortByAccessor(screens, SCREEN_SORT_ACCESSORS[sortState.columnId], sortState.direction),
    [screens, sortState]
  );
  const rows = useMemo(() => paginate(sortedScreens, page, pageSize), [sortedScreens, page, pageSize]);

  const cells: Record<TopScreensColumnId, ColumnCell> = {
    actions: screen => (
      <span className="topScreensList__colActions" data-row-action>
        <ScreenMapActions
          id={`topScreensList-mapActions-${toScreenDomId(screen.id)}`}
          poiLocation={screen.nearbyPoi?.location ?? null}
          screenLocation={screen.location}
        />
        <ScreenTechSpecs id={`topScreensList-techSpecs-${toScreenDomId(screen.id)}`} screen={screen} />
      </span>
    ),
    estimatedAudience: screen => <span>{formatCount(screen.estimatedAudience)}</span>,
    impressions: screen => <span>{formatCount(screen.impressions)}</span>,
    onTarget: screen => <span>{formatPercent(screen.onTargetPct)}</span>,
    ota: screen => (
      <span>
        {screen.ota === null ? (
          notAvailable
        ) : (
          <Tag id={`topScreensList-ota-${toScreenDomId(screen.id)}`} status="ok">
            {formatOta(screen.ota)}
          </Tag>
        )}
      </span>
    ),
    playouts: screen => <span>{formatCount(screen.playouts)}</span>,
    poi: screen => (
      <span className="topScreensList__colPoi">
        <PoiBadge nearbyPoi={screen.nearbyPoi} tagId={`topScreensList-poi-${toScreenDomId(screen.id)}`} />
      </span>
    ),
    publisher: screen => {
      const publisher = screen.publisher ?? notAvailable;
      return (
        <HoverTooltip content={publisher}>
          <span className="topScreensList__clamped">{publisher}</span>
        </HoverTooltip>
      );
    },
    resolution: screen => <span>{screen.techSpecs.resolution ?? notAvailable}</span>,
    screenFormat: screen => {
      const screenFormat = screen.techSpecs.screenTypes.join(', ') || notAvailable;
      return (
        <HoverTooltip content={screenFormat}>
          <span className="topScreensList__clamped">{screenFormat}</span>
        </HoverTooltip>
      );
    },
    spotLength: screen => (
      <span>
        {screen.techSpecs.spotLengthSeconds === null
          ? notAvailable
          : formatMessage(
              { defaultMessage: '{seconds}s', id: 'doohReport.deliveryDrivers.spotLength' },
              { seconds: screen.techSpecs.spotLengthSeconds }
            )}
      </span>
    ),
    venueType: screen => {
      const venueTypes = screen.venueTypes.join(', ') || notAvailable;
      return (
        <HoverTooltip content={venueTypes}>
          <span className="topScreensList__clamped">{venueTypes}</span>
        </HoverTooltip>
      );
    },
    zone: screen => {
      const screenName = screen.name ?? notAvailable;
      return (
        <>
          <span className="topScreensList__colRank">{screen.rank}</span>
          <div className="topScreensList__colZone">
            <HoverTooltip content={screenName}>
              <span className="topScreensList__name">{screenName}</span>
            </HoverTooltip>
            {screen.address && (
              <HoverTooltip content={screen.address}>
                <span className="topScreensList__address">{screen.address}</span>
              </HoverTooltip>
            )}
          </div>
        </>
      );
    }
  };

  const visibleColumns = TOP_SCREENS_COLUMNS.filter(({ hideable, id }) => !hideable || isColumnVisible(id));
  const gridStyle = {
    '--topScreensList-columns': buildTopScreensGridColumns(isColumnVisible)
  } as CSSProperties;

  return (
    <>
      <div className="topScreensList__listWrapper" style={gridStyle}>
        <TaptapList<DoohReportScreen>
          className="topScreensList__list"
          data={rows}
          id="topScreensList"
          onSortChange={(columnId, direction) => {
            if (isDeliveryDriversSortableColumn(columnId)) {
              setSortState({ columnId, direction });
              setPage(1);
            }
          }}
        >
          <TaptapList.ColumnsBar>
            {visibleColumns.map(({ id }) => (
              <TaptapList.ColumnsBar.Column
                className={id === 'zone' ? 'topScreensList__colZone' : undefined}
                description={id === 'actions' ? '' : labels[id]}
                id={id}
                key={id}
                sortable={isDeliveryDriversSortableColumn(id)}
              />
            ))}
          </TaptapList.ColumnsBar>
          <TaptapList.RowsList<DoohReportScreen>
            emptyStateIcon="fal fa-empty-set"
            emptyStateTitle={formatMessage({
              defaultMessage: 'There is no screen data for this circuit yet.',
              id: 'doohReport.deliveryDrivers.emptyState.title'
            })}
          >
            {itemsList =>
              itemsList.map(screen => (
                <TaptapList.RowsList.Row id={screen.id} key={screen.id}>
                  <div className="topScreensList__row">
                    {visibleColumns.map(({ id }) => (
                      <Fragment key={id}>{cells[id](screen)}</Fragment>
                    ))}
                  </div>
                </TaptapList.RowsList.Row>
              ))
            }
          </TaptapList.RowsList>
        </TaptapList>
      </div>
      <ListPagination
        id="topScreensList-pagination"
        onPageChange={setPage}
        onPageSizeChange={newPageSize => {
          setPageSize(newPageSize);
          setPage(1);
        }}
        page={page}
        pageSize={pageSize}
        totalItems={screens.length}
      />
    </>
  );
}
